import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, Upload, Settings, LogOut, Menu, Bell, Shield, ChevronRight, Users, Activity } from 'lucide-react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

const Sidebar = ({ isOpen, setIsOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    // Move auth logic here so it's accessible
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const menuItems = [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: FileText, label: 'My Documents', path: '/documents' },
        { icon: Upload, label: 'Upload & Scan', path: '/upload' },
        { icon: Users, label: 'Family & Access', path: '/family' },
        { icon: Activity, label: 'Activity Logs', path: '/audit' },
        { icon: Settings, label: 'Settings', path: '/settings' },
    ];

    const sidebarVariants = {
        closed: { x: '-100%', transition: { type: 'spring', stiffness: 300, damping: 30 } },
        open: { x: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } }
    };

    return (
        <>
            {/* Mobile Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm lg:hidden"
                        onClick={() => setIsOpen(false)}
                    />
                )}
            </AnimatePresence>
            
            {/* Sidebar */}
            <motion.div
                variants={sidebarVariants}
                initial="closed"
                animate={isOpen ? 'open' : (window.innerWidth >= 1024 ? 'open' : 'closed')}
                className={clsx(
                    'fixed inset-y-0 left-0 z-30 w-72 bg-white/90 dark:bg-gray-900/95 backdrop-blur-xl border-r border-gray-200 dark:border-gray-800 lg:static lg:translate-x-0 shadow-2xl lg:shadow-none',
                )}
            >
                <div className="flex h-20 items-center px-8 border-b border-gray-100 dark:border-gray-800/50">
                    <Shield className="h-8 w-8 text-blue-600 mr-3" />
                    <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                        SecureDocs
                    </span>
                </div>

                <nav className="mt-8 px-4 space-y-2">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => window.innerWidth < 1024 && setIsOpen(false)}
                                className={clsx(
                                    'group flex items-center justify-between rounded-xl px-4 py-3.5 text-sm font-medium transition-all duration-200',
                                    isActive
                                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white'
                                )}
                            >
                                <div className="flex items-center">
                                    <Icon className={clsx('mr-3 h-5 w-5', isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300')} />
                                    {item.label}
                                </div>
                                {isActive && <ChevronRight className="h-4 w-4 text-white/50" />}
                            </Link>
                        );
                    })}
                </nav>

                <div className="absolute bottom-0 left-0 right-0 p-6 space-y-3">
                    {user?.role === 'ADMIN' && (
                        <button
                            onClick={() => navigate('/admin')}
                            className="flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-500/30 transition-all hover:scale-[1.02]"
                        >
                            <Shield className="mr-2 h-5 w-5" />
                            Admin Panel
                        </button>
                    )}
                    <button
                        onClick={() => navigate('/emergency')}
                        className="flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-500/30 transition-all hover:scale-[1.02]"
                    >
                        <Shield className="mr-2 h-5 w-5" />
                        Emergency Mode
                    </button>
                    <button
                        onClick={logout}
                        className="flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <LogOut className="mr-2 h-5 w-5" />
                        Sign Out
                    </button>
                </div>
            </motion.div>
        </>
    );
};

const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    return (
        <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden font-sans">
            <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

            <div className="flex flex-1 flex-col overflow-hidden relative">
                {/* Topbar */}
                <header className="flex h-20 items-center justify-between bg-white/80 dark:bg-gray-900/80 backdrop-blur-md px-6 lg:px-8 z-10 sticky top-0">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                        <Menu className="h-6 w-6" />
                    </button>

                    <div className="flex flex-1 justify-end items-center space-x-6">
                        <button className="relative text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
                            <Bell className="h-6 w-6" />
                            <span className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-white dark:border-gray-900"></span>
                        </button>
                        
                        <div className="flex items-center pl-6 border-l border-gray-200 dark:border-gray-700">
                            <div className="flex flex-col items-end mr-3 hidden md:flex">
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {user.email?.split('@')[0] || 'User'}
                                </span>
                                <span className="text-xs text-gray-500 uppercase tracking-wider">
                                    {user.role || 'Member'}
                                </span>
                            </div>
                            <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold shadow-lg ring-2 ring-white dark:ring-gray-800">
                                {user.email ? user.email[0].toUpperCase() : 'U'}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-1 overflow-y-auto p-4 lg:p-8 relative">
                    {/* Background Decorative Blobs */}
                    <div className="fixed top-20 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none -z-10" />
                    <div className="fixed bottom-0 left-0 w-[500px] h-[500px] bg-purple-400/10 rounded-full blur-3xl pointer-events-none -z-10" />
                    
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
